import React, { useEffect, useState, useReducer, useContext } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Pagination from 'react-bootstrap/Pagination';
import { Store } from '../Store';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import { getError } from '../utils';
import "./DataTable.css";

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        report: action.payload.report,
        page: action.payload.page,
        pages: action.payload.pages,
        count: action.payload.count,
        loading: false
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function Report() {
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [{ loading, error, report, pages, count }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    report: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await axios.get(
          `/api/report/all?page=${page}&search=${query}&sortBy=${sortBy}&sortOrder=${sortOrder}`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (error) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(error),
        });
      }
    };
    fetchData();
  }, [userInfo, page, query, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <i className="fas fa-sort text-muted ms-1 small"></i>;
    return sortOrder === "asc" ?
      <i className="fas fa-sort-up ms-1 text-primary"></i> :
      <i className="fas fa-sort-down ms-1 text-primary"></i>;
  };

  return (
    <Container className="py-4">
      <Helmet>
        <title>Sales Report</title>
      </Helmet>

      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="fw-bold mb-0 text-dark">Sales Reports</h1>
          <p className="text-muted mb-0">Detailed performance and profitability tracking ({count || 0} entries)</p>
        </Col>
        <Col md={4}>
          <InputGroup className="search-bar-container shadow-sm">
            <InputGroup.Text className="bg-white border-end-0">
              <i className="fas fa-search text-muted"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search comments or ID..."
              className="border-start-0 ps-0 pe-3 py-2"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </InputGroup>
        </Col>
      </Row>

      {loading ? (
        <LoadingBox></LoadingBox>
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <div className="data-table-container border-0 shadow-sm">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("createdAt")} className={sortBy === "createdAt" ? "active-sort" : ""}>
                    DATE {getSortIcon("createdAt")}
                  </th>
                  <th onClick={() => handleSort("sales")} className={sortBy === "sales" ? "active-sort" : ""}>
                    SALES {getSortIcon("sales")}
                  </th>
                  <th onClick={() => handleSort("costs")} className={sortBy === "costs" ? "active-sort" : ""}>
                    COSTS {getSortIcon("costs")}
                  </th>
                  <th onClick={() => handleSort("grossProfit")} className={sortBy === "grossProfit" ? "active-sort" : ""}>
                    GROSS PROFIT {getSortIcon("grossProfit")}
                  </th>
                  <th onClick={() => handleSort("netProfit")} className={sortBy === "netProfit" ? "active-sort" : ""}>
                    NET PROFIT {getSortIcon("netProfit")}
                  </th>
                  <th onClick={() => handleSort("depts")} className={sortBy === "depts" ? "active-sort" : ""}>
                    SEPTS {getSortIcon("depts")}
                  </th>
                  <th>ITEMS</th>
                  <th className="text-center">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {report.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="fw-bold text-dark">{new Date(item.createdAt).toLocaleDateString()}</div>
                      <div className="text-muted extra-small" style={{ fontSize: '0.65rem' }}>#{item._id.substring(item._id.length - 6)}</div>
                    </td>
                    <td className="fw-bold text-success">{item.sales.toLocaleString()} RWF</td>
                    <td className="text-muted">{item.costs.toLocaleString()} RWF</td>
                    <td className="fw-bold" style={{ color: '#ffc107' }}>{item.grossProfit.toLocaleString()} RWF</td>
                    <td className="fw-bold text-primary">{item.netProfit.toLocaleString()} RWF</td>
                    <td className={item.depts > 0 ? "text-danger fw-bold" : "text-muted"}>
                      {item.depts.toLocaleString()} RWF
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {item.reportItems.length} Products
                      </span>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="light"
                        size="sm"
                        className="rounded-pill me-1 shadow-sm px-3"
                        onClick={() => navigate(`/report/${item._id}`)}
                      >
                        Details
                      </Button>
                      <Button
                        variant="soft-primary"
                        size="sm"
                        className="rounded-pill shadow-sm"
                        style={{ backgroundColor: '#e7f1ff', color: '#0d6efd', border: 'none' }}
                        onClick={() => navigate(`/update/${item._id}`)}
                      >
                        Update
                      </Button>
                    </td>
                  </tr>
                ))}
                {report.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted italic">
                      No report records found for this criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="pagination-container pt-3">
              <Pagination size="sm">
                <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
                <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
                {[...Array(pages).keys()].map((x) => (
                  <Pagination.Item
                    key={x + 1}
                    active={x + 1 === page}
                    onClick={() => setPage(x + 1)}
                  >
                    {x + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next disabled={page === pages} onClick={() => setPage(page + 1)} />
                <Pagination.Last disabled={page === pages} onClick={() => setPage(pages)} />
              </Pagination>
            </div>
          )}
        </div>
      )}
    </Container>
  );
}
